import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Switch,
    FormControlLabel,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Alert,
    CircularProgress,
    Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import InfoIcon from '@mui/icons-material/Info';
import api from '../../services/api';

function SummaryInstructions() {
    const [instructions, setInstructions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        instruction_text: '',
        priority: 0,
        is_active: true
    });

    useEffect(() => {
        fetchInstructions();
    }, []);

    const fetchInstructions = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/summary-instructions');
            setInstructions(response.data.data || []);
            setError('');
        } catch (err) {
            setError('Failed to load instructions');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (instruction = null) => {
        if (instruction) {
            setEditingId(instruction.id);
            setFormData({
                instruction_text: instruction.instruction_text,
                priority: instruction.priority,
                is_active: instruction.is_active
            });
        } else {
            setEditingId(null);
            setFormData({
                instruction_text: '',
                priority: instructions.length,
                is_active: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingId(null);
        setFormData({
            instruction_text: '',
            priority: 0,
            is_active: true
        });
    };

    const handleSave = async () => {
        try {
            if (!formData.instruction_text.trim()) {
                setError('Instruction text is required');
                return;
            }

            if (editingId) {
                await api.put(`/api/summary-instructions/${editingId}`, formData);
                setSuccess('Instruction updated successfully');
            } else {
                await api.post('/api/summary-instructions', formData);
                setSuccess('Instruction created successfully');
            }

            handleCloseDialog();
            fetchInstructions();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to save instruction');
            console.error('Error:', err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this instruction?')) {
            return;
        }

        try {
            await api.delete(`/api/summary-instructions/${id}`);
            setSuccess('Instruction deleted successfully');
            fetchInstructions();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError('Failed to delete instruction');
            console.error('Error:', err);
        }
    };

    const handleToggleActive = async (instruction) => {
        try {
            await api.put(`/api/summary-instructions/${instruction.id}`, {
                is_active: !instruction.is_active
            });
            fetchInstructions();
        } catch (err) {
            setError('Failed to update status');
            console.error('Error:', err);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    Summary Instructions
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Add Instruction
                </Button>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                    {success}
                </Alert>
            )}

            <Paper sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <InfoIcon color="primary" sx={{ mt: 0.5 }} />
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            About Summary Instructions
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            These instructions guide how AI generates customer summaries. They are included in the AI prompt
                            when analyzing conversations. Use them to focus on specific aspects like customer issues, sales opportunities,
                            or complaint severity. Instructions are applied in priority order (lower number = higher priority).
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Priority</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Instruction</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {instructions.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No instructions yet. Click "Add Instruction" to create one.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            instructions.map((instruction) => (
                                <TableRow key={instruction.id} hover>
                                    <TableCell>
                                        <Chip 
                                            label={instruction.priority} 
                                            size="small" 
                                            color="primary" 
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {instruction.instruction_text}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={instruction.is_active}
                                                    onChange={() => handleToggleActive(instruction)}
                                                    size="small"
                                                />
                                            }
                                            label={instruction.is_active ? 'Active' : 'Inactive'}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton
                                            size="small"
                                            color="primary"
                                            onClick={() => handleOpenDialog(instruction)}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={() => handleDelete(instruction.id)}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Add/Edit Dialog */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {editingId ? 'Edit Instruction' : 'Add New Instruction'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <TextField
                            label="Instruction Text"
                            multiline
                            rows={4}
                            fullWidth
                            value={formData.instruction_text}
                            onChange={(e) => setFormData({ ...formData, instruction_text: e.target.value })}
                            placeholder="e.g., Focus on identifying customer pain points and urgent issues"
                            required
                        />
                        <TextField
                            label="Priority"
                            type="number"
                            fullWidth
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                            helperText="Lower number = higher priority (0 is highest)"
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        startIcon={<SaveIcon />}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default SummaryInstructions;
